import React from "react";
import { useSelector } from "react-redux";
import {
  getAgencyShortNameByAuctionPenId,
  getAuctionPenById,
  getSaleLotIdsByAuctionPenId,
  getStatusByAuctionPenId,
} from "selectors";
import Badge from "components/Badge";
import { Column, Row } from "components/Layout";
import { CombinedMissingFieldIndicator } from "components/MissingFieldsIndicator";
import { NeverEverAuctionPenStatus } from "components/NeverEver/NeverEverStatus";

import { AuctionPenStatus } from "constants/auctionPens";

import { ForSaleyardAdmin } from "containers/ForUserType";
import { getOverridePenStatus } from "lib/auctionPens";
import { AgencyName } from "./elements";
import { Actions } from "./Actions";

interface AuctionPenCardActionsSectionProps {
  auctionPenId: string;
}

const AuctionPenStatusColorMap = {
  [AuctionPenStatus.NOT_COUNTED]: "inputError",
  [AuctionPenStatus.NOT_PENNED]: "warning",
  [AuctionPenStatus.TEMP_PEN]: "warning",
  [AuctionPenStatus.NOT_SOLD]: "inProgress",
  [AuctionPenStatus.PARTIALLY_SOLD]: "primary",
  [AuctionPenStatus.SOLD]: "success",
  [AuctionPenStatus.NO_SALE]: "noProgress",
  [AuctionPenStatus.PARTIALLY_DELIVERED]: "primary",
  [AuctionPenStatus.DELIVERED]: "primary",
};

function getBadgeColor(status: AuctionPenStatus): string {
  return AuctionPenStatusColorMap[status] || "primary";
}

export function AuctionPenCardActionsSection(
  props: AuctionPenCardActionsSectionProps,
) {
  const { auctionPenId } = props;

  const penStatus = useSelector(getStatusByAuctionPenId(auctionPenId));
  const auctionPen: AuctionPen = useSelector(getAuctionPenById(auctionPenId));

  // Force the status of the unpenned pen (auctionPenId null) to be ... unpenned (Count is a higher status, so was trumping it)
  const status: AuctionPenStatus = getOverridePenStatus(
    auctionPen,
    auctionPenId,
    penStatus,
  );

  const agencyShortName: string = useSelector<State, string>(
    getAgencyShortNameByAuctionPenId(auctionPenId),
  );

  const saleLotIds: string[] =
    useSelector<RootState, string[]>(
      getSaleLotIdsByAuctionPenId(auctionPenId),
    ) || [];

  const badgeColor: string = getBadgeColor(status);

  return (
    <Row alignCenter>
      <Column>
        <Badge
          color={badgeColor}
          uppercase
          data-tour={status}
          partially={
            !![
              AuctionPenStatus.PARTIALLY_SOLD,
              AuctionPenStatus.PARTIALLY_DELIVERED,
            ].includes(status)
          }
        >
          {status}
        </Badge>
      </Column>
      <Column paddingHorizontal={2} flexGrow flex={false}>
        <ForSaleyardAdmin>
          <AgencyName>{agencyShortName}</AgencyName>
        </ForSaleyardAdmin>
      </Column>
      <CombinedMissingFieldIndicator modelIds={saleLotIds} />

      <Column paddingHorizontal={2} flex={false}>
        <NeverEverAuctionPenStatus auctionPenId={auctionPenId} />
      </Column>
      <Column flex={false}>
        <Actions auctionPenId={auctionPenId} />
      </Column>
    </Row>
  );
}
